import { ChangeDetectorRef, Component, Input } from '@angular/core';

import moment from 'moment-timezone';

import { FieldKind } from '@recruitee/custom-fields-types';
import type {
  Education,
  Experience,
  Field,
  FieldValue,
  LanguageValue,
  NationalityValue,
} from '@recruitee/custom-fields-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { IconComponent } from '@recruitee/user-interface';

@Component({
  selector: 'rt-activity-profile-field-updated',
  templateUrl: './activity-profile-field-updated.component.html',
  styleUrls: ['./activity-profile-field-updated.component.less'],
  imports: [IconComponent, TranslatePipe],
})
export class ActivityProfileFieldUpdatedComponent {
  public readonly booleanTranslations: { [key: string]: string } = {
    ['true']: this.i18n.translate('app.common.boolean.yes'),
    ['false']: this.i18n.translate('app.common.boolean.no'),
  };

  public readonly genderTranslations: { [key: string]: string } = {
    male: this.i18n.translate('app.custom_fields.gender_options.male'),
    female: this.i18n.translate('app.custom_fields.gender_options.female'),
    non_binary: this.i18n.translate('app.custom_fields.gender_options.non_binary'),
    other: this.i18n.translate('app.custom_fields.gender_options.other'),
  };

  public readonly levelsTranslations: { [key: string]: string } = {
    advanced: this.i18n.translate('app.language_skills.advanced'),
    basic: this.i18n.translate('app.language_skills.basic'),
    intermediate: this.i18n.translate('app.language_skills.intermediate'),
    native: this.i18n.translate('app.language_skills.native'),
    near_native: this.i18n.translate('app.language_skills.near_native'),
    none: this.i18n.translate('app.language_skills.none'),
  };

  @Input() public previousField: Field;
  @Input() public currentField: Field;

  public collapsed: boolean = true;
  public initialized: boolean = false;
  public previousContent: string = '';
  public currentContent: string = '';

  constructor(
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
  ) {}

  public toggle(event: MouseEvent): void {
    event.preventDefault();
    if (!this.initialized) {
      this.previousContent = this.mapFieldForDiff(
        this.previousField.values,
        this.previousField.kind,
      );
      this.currentContent = this.mapFieldForDiff(this.currentField.values, this.previousField.kind);
      this.initialized = true;
    }
    this.collapsed = !this.collapsed;
    this.cdRef.detectChanges();
  }

  private mapFieldForDiff(value: FieldValue, kind: FieldKind): string {
    switch (kind) {
      case FieldKind.Nationality:
        return (<NationalityValue>value).map(v => v.countryName || v.countryCode).join(', ');
      case FieldKind.LanguageSkill:
        return (<LanguageValue>value)
          .map(
            v =>
              `${v.languageName || v.languageCode}${
                v.level ? ' - ' + this.levelsTranslations[v.level] : ''
              }`,
          )
          .join(', ');
      case FieldKind.Experience:
        return [...value]
          .sort(this.sortByStartDate)
          .map(v => this.getExperienceParsed(<Experience>v))
          .join('\n\n');
      case FieldKind.Education:
        return [...value]
          .sort(this.sortByStartDate)
          .map(v => this.getEducationParsed(<Education>v))
          .join('\n\n');
      default:
        return value
          .map(v =>
            Object.values(v)
              .map(_v => this.getDiffDisplayValue(<string>_v, kind))
              .filter(_v => !!_v)
              .join(', '),
          )
          .join(', ');
    }
  }

  private getExperienceParsed(experience: Experience): string {
    const { title, company, startDate, endDate, location, description } = experience;
    let parsed = title;

    if (company) {
      parsed += `\n${company}`;
    }
    if (startDate) {
      parsed += `\n${moment(startDate).format('MMM YYYY')}`;
      if (endDate) {
        parsed += ` - ${moment(endDate).format('MMM YYYY')}`;
      } else {
        parsed += ` - ${this.i18n.translate('app.common.present')}`;
      }
    }
    if (location) {
      parsed += `\n${location}`;
    }
    if (description) {
      parsed += `\n${description}`;
    }
    return parsed;
  }

  private getEducationParsed(education: Education): string {
    const { school, degree, major, startDate, endDate, description } = education;
    let parsed = school;

    if (degree) {
      parsed += `\n${degree}`;
    }
    if (major) {
      parsed += `\n${major}`;
    }
    if (startDate) {
      parsed += `\n${moment(startDate).format('YYYY')}`;
      if (endDate) {
        parsed += ` - ${moment(endDate).format('YYYY')}`;
      } else {
        parsed += ` - ${this.i18n.translate('app.common.present')}`;
      }
    }
    if (description) {
      parsed += `\n${description}`;
    }
    return parsed;
  }

  private getDiffDisplayValue(value: string, kind: FieldKind): string {
    switch (kind) {
      case FieldKind.Boolean:
        return this.booleanTranslations[value];
      case FieldKind.Gender:
        return this.genderTranslations[value];
      default:
        return value;
    }
  }

  private sortByStartDate(a: Experience | Education, b: Experience | Education): number {
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  }
}
